import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { DocumentCategory } from "../services/documentService";
import { Progress } from "@/components/ui/progress";
import { useState, useEffect } from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Info } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";

interface DocumentUploadProps {
  buildingId: string;
  onUploadComplete?: () => void;
  onDataExtracted?: (data: any) => void;
  selectedCategory?: DocumentCategory;
  currentCategory?: "drawings" | "documents" | "unsorted";
  currentFolder?: string | null;
  onRefresh?: () => Promise<void>;
  fileProgresses?: Array<{
    id: string;
    name: string;
    progress: number;
    status: "uploading" | "processing" | "complete" | "failed";
  }>;
  projectId?: string;
}

export function DocumentUpload({
  buildingId,
  onUploadComplete,
  onDataExtracted,
  selectedCategory = "documents",
  currentCategory,
  currentFolder,
  onRefresh,
  fileProgresses: externalFileProgresses,
  projectId,
}: DocumentUploadProps) {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();

  useEffect(() => {
    // Validate prerequisites for upload
    if (!userProfile?.id) {
      setError(t.documents.classification.userProfileError);
      return;
    }

    if (!currentOrganization?.id) {
      setError(t.documents.classification.organizationError);
      return;
    }

    // Clear error if all prerequisites are met
    setError(null);
  }, [userProfile, currentOrganization]);

  // Use external props if provided, otherwise use own hook
  const ownHook = useDocumentUpload({
    buildingId,
    onUploadComplete,
    onDataExtracted,
    initialCategory: selectedCategory,
    projectId,
    requireProject: true,
  });

  const fileProgresses = externalFileProgresses ?? ownHook.fileProgresses;
  const handleFileUpload = ownHook.handleFileUpload;

  // File selection and drop handlers removed - handled at page level

  return (
    <div className="mx-auto w-full max-w-2xl p-4">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t.documents.classification.uploadNotAvailable}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {(!userProfile?.id || !currentOrganization?.id) && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>{t.documents.classification.setupRequired}</AlertTitle>
          <AlertDescription>
            {!userProfile?.id && `${t.documents.classification.loginPrompt} `}
            {!currentOrganization?.id && t.documents.classification.organizationPrompt}
          </AlertDescription>
        </Alert>
      )}

      {fileProgresses.length > 0 && (
        <div className="mt-4 space-y-4">
          {fileProgresses.map((fp) => (
            <div key={fp.id} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>{fp.name}</span>
                <span
                  className={`text-muted-foreground ${fp.status === "failed" ? "text-destructive" : ""}`}
                >
                  {fp.status === "uploading"
                    ? t.documents.uploadProgress.uploading
                    : fp.status === "processing"
                      ? t.common.processing
                      : fp.status === "complete"
                        ? t.documents.uploadProgress.complete
                        : t.documents.uploadProgress.failed}
                </span>
              </div>
              <Progress
                value={fp.progress}
                className={fp.status === "failed" ? "bg-destructive/20" : ""}
              />
              {fp.status === "failed" && (
                <p className="mt-1 text-xs text-destructive">{t.documents.uploadFailedMessage}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
